import React from 'react';
import { Button, Dropdown, Flex, Switch, theme, Typography, type MenuProps } from 'antd';
import { CheckOutlined, CloseOutlined, DownloadOutlined, LoadingOutlined, MoreOutlined } from '@ant-design/icons';

type ActionMenuItem = Exclude<Required<MenuProps>['items'][number], null> & {
  action?: () => void;
};

export type MoreActionsExportConfig = {
  label: string;
  disabled?: boolean;
  loading?: boolean;
  onExportAsPdf: () => void;
};

// Both items are optional so a page can mount the menu with only the actions it
// has implemented, rather than having to supply every one.
type MoreActionsDropdownProps = {
  export?: MoreActionsExportConfig;
  chartDescriptions?: {
    enabled: boolean;
    onChange: (enabled: boolean) => void;
  };
};

function ChartDescriptionsToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (enabled: boolean) => void;
}) {
  const { token } = theme.useToken();

  return (
    <Flex align="center" gap={token.marginXS}>
      <Switch
        checked={checked}
        onChange={onChange}
        onClick={(_, event) => event.stopPropagation()}
        checkedChildren={<CheckOutlined />}
        unCheckedChildren={<CloseOutlined />}
      />
      <Typography.Text style={{ fontSize: token.fontSize, lineHeight: token.lineHeight }}>
        Chart Descriptions
      </Typography.Text>
    </Flex>
  );
}

function MoreActionsDropdown({
  export: exportConfig,
  chartDescriptions,
}: MoreActionsDropdownProps) {
  const { token } = theme.useToken();

  const actionItems = React.useMemo<ActionMenuItem[]>(
    () => {
      const items: ActionMenuItem[] = [];

      if (exportConfig) {
        items.push({
          key: 'export-as-pdf',
          label: exportConfig.label,
          icon: exportConfig.loading ? <LoadingOutlined spin /> : <DownloadOutlined />,
          disabled: exportConfig.disabled || exportConfig.loading,
          action: exportConfig.disabled || exportConfig.loading ? undefined : exportConfig.onExportAsPdf,
        });
      }

      if (chartDescriptions) {
        items.push({
          key: 'chart-descriptions',
          label: <ChartDescriptionsToggle checked={chartDescriptions.enabled} onChange={chartDescriptions.onChange} />,
          style: { paddingBlock: token.paddingXS, paddingInline: token.paddingXXS, height: 'auto', margin: 0 },
          action: () => chartDescriptions.onChange(!chartDescriptions.enabled),
        });
      }

      return items;
    },
    [
      exportConfig,
      chartDescriptions,
      token.paddingXS,
      token.paddingXXS,
    ]
  );

  // The divider only makes sense between two items: with a single configured
  // action it would render as a stray line, and with none the menu is empty.
  const menuItems = React.useMemo<MenuProps['items']>(
    () => {
      const [firstItem, ...remainingItems] = actionItems.map(({ action, ...menuItem }) => menuItem);

      if (!firstItem) return [];
      if (!remainingItems.length) return [firstItem];

      return [firstItem, { type: 'divider' }, ...remainingItems];
    },
    [actionItems]
  );

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    const clickedItem = actionItems.find((item) => item.key === key);
    clickedItem?.action?.();
  };

  return (
    <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} trigger={['click']} placement="bottomRight">
      <Button icon={<MoreOutlined />} />
    </Dropdown>
  );
}

export default MoreActionsDropdown;
